<template>
    <div class="ordering-board">
        <transition name="board-loader-fade" mode="out-in">
            <ordering-table-board-intro-loader v-if="showIntroLoader" key="board-loader" />

            <div v-else key="board-content" class="board-content">
                <section class="board-hero">
                    <div>
                        <div class="board-eyebrow">Commande à table</div>
                        <h1 class="board-title">{{ restaurantName }}</h1>
                        <p class="board-subtitle">Vue d'ensemble des commandes récentes, des statuts de service et des accès rapides du module.</p>
                    </div>

                    <div class="board-hero__actions">
                        <div class="board-updated" v-if="lastUpdatedAt">Mis à jour à {{ formatTime(lastUpdatedAt) }}</div>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary btn-circle"
                            :disabled="loading || refreshing"
                            @click="fetchDashboard">
                            <i :class="['fa', refreshing ? 'fa-spinner fa-spin' : 'fa-refresh', 'me-2']"></i>
                            Actualiser
                        </button>
                    </div>
                </section>

                <div class="board-range-switcher" role="tablist" aria-label="Période du tableau de bord">
                    <button
                        v-for="option in rangeOptions"
                        :key="option.value"
                        type="button"
                        :class="['board-range-switcher__button', { 'board-range-switcher__button--active': activeRange === option.value }]"
                        :aria-pressed="activeRange === option.value"
                        @click="activeRange = option.value">
                        {{ option.label }}
                    </button>
                </div>

                <div v-if="meta.capped" class="alert alert-warning board-alert">
                    L'API a renvoyé un maximum de 200 commandes récentes. Les indicateurs reflètent cet échantillon.
                </div>

                <div v-if="loading" class="board-state board-state-loading">
                    <div class="noshow-loader" aria-hidden="true">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <span>Chargement du tableau de bord…</span>
                </div>

                <div v-else-if="error" class="board-state board-state-error">
                    {{ error }}
                </div>

                <template v-else>
                    <section class="board-stats">
                        <article v-for="card in statCards" :key="card.label" :class="['stat-card', `stat-card--${card.tone}`]">
                            <div class="stat-card__label">{{ card.label }}</div>
                            <div class="stat-card__value">{{ card.value }}</div>
                            <div class="stat-card__meta">{{ card.meta }}</div>
                        </article>
                    </section>

                    <section class="board-grid">
                        <article class="board-panel board-panel--wide">
                            <div class="board-panel__header">
                                <div>
                                    <h2>Flux de commandes</h2>
                                    <p>{{ trendTitle }}</p>
                                </div>
                            </div>
                            <div v-if="hasTrendData" class="chart-shell">
                                <VChart class="chart" :option="flowChartOption" autoresize />
                            </div>
                            <div v-else class="panel-empty">Aucune commande disponible sur la période pour tracer l'évolution.</div>
                        </article>

                        <article class="board-panel">
                            <div class="board-panel__header">
                                <div>
                                    <h2>Répartition des statuts</h2>
                                    <p>{{ periodLabel }}</p>
                                </div>
                            </div>
                            <div v-if="hasFilteredOrders" class="chart-shell chart-shell--compact">
                                <VChart class="chart" :option="statusChartOption" autoresize />
                            </div>
                            <div v-else class="panel-empty">Aucune commande sur cette période.</div>
                        </article>

                        <article class="board-panel">
                            <div class="board-panel__header">
                                <div>
                                    <h2>Top tables</h2>
                                    <p>Commandes agrégées par table ou point d'entrée</p>
                                </div>
                            </div>
                            <div v-if="topTables.length" class="chart-shell chart-shell--compact">
                                <VChart class="chart" :option="topTablesChartOption" autoresize />
                            </div>
                            <div v-else class="panel-empty">Aucune table active à afficher sur la période.</div>
                        </article>

                        <article class="board-panel board-panel--wide">
                            <div class="board-panel__header">
                                <div>
                                    <h2>Commandes récentes</h2>
                                    <p>{{ recentOrdersSubtitle }}</p>
                                </div>
                            </div>

                            <div v-if="recentOrders.length" class="recent-orders">
                                <div v-for="order in recentOrders" :key="order.id" class="recent-order">
                                    <div class="recent-order__main">
                                        <div class="recent-order__title">
                                            <span class="recent-order__id">#{{ order.id }}</span>
                                            <span>{{ getTable(order) }}</span>
                                        </div>
                                        <div class="recent-order__meta">
                                            <span v-if="order.beeper_number != null">Beeper / chevalet : {{ order.beeper_number }} · </span>
                                            {{ order.customer_name || "Sans nom" }} · {{ itemCount(order) }} article{{
                                                itemCount(order) > 1 ? "s" : ""
                                            }}
                                        </div>
                                    </div>
                                    <div class="recent-order__side">
                                        <span :class="['status-pill', `status-pill--${statusTone(order.status)}`]">{{
                                            statusLabel(order.status)
                                        }}</span>
                                        <div class="recent-order__amount">{{ formatMoney(order.total_cents) }}</div>
                                        <div class="recent-order__date">{{ formatDate(order.created_at) }}</div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="panel-empty">Aucune commande récente à afficher.</div>
                        </article>

                        <article class="board-panel">
                            <div class="board-panel__header">
                                <div>
                                    <h2>Accès rapides</h2>
                                    <p>Raccourcis utiles pour l'exploitation quotidienne</p>
                                </div>
                            </div>

                            <div class="quick-links">
                                <router-link v-for="link in quickLinks" :key="link.title" :to="link.to" class="quick-link">
                                    <span class="quick-link__icon">
                                        <i :class="link.icon"></i>
                                    </span>
                                    <span class="quick-link__content">
                                        <span class="quick-link__title">{{ link.title }}</span>
                                        <span class="quick-link__description">{{ link.description }}</span>
                                    </span>
                                </router-link>
                            </div>
                        </article>
                    </section>
                </template>
            </div>
        </transition>
    </div>
</template>

<script>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import VChart from "vue-echarts";
import OrderingTableBoardIntroLoader from "./Board/components/OrderingTableBoardIntroLoader.vue";

use([CanvasRenderer, BarChart, LineChart, PieChart, GridComponent, LegendComponent, TooltipComponent]);

const RANGE_OPTIONS = [
    { value: "today", label: "Aujourd'hui", days: 1 },
    { value: "7d", label: "7 jours", days: 7 },
    { value: "30d", label: "30 jours", days: 30 },
];

const STATUS_META = {
    created: { label: "Créée", color: "#f59e0b", tone: "warning" },
    validated: { label: "Validée", color: "#10b981", tone: "success" },
    preparing: { label: "En préparation", color: "#f97316", tone: "warning" },
    ready: { label: "Prête", color: "#3b82f6", tone: "info" },
    served: { label: "Servie", color: "#6366f1", tone: "neutral" },
    paid: { label: "Payée", color: "#0f766e", tone: "success" },
    canceled: { label: "Annulée", color: "#ef4444", tone: "danger" },
    failed: { label: "Échouée", color: "#b91c1c", tone: "danger" },
};

const OPEN_STATUSES = ["created", "validated", "preparing", "ready"];
const EXCLUDED_AMOUNT_STATUSES = ["canceled", "failed"];

const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const dayKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default {
    name: "OrderingTableBoard",
    components: {
        OrderingTableBoardIntroLoader,
        VChart,
    },
    data() {
        return {
            orders: [],
            loading: true,
            refreshing: false,
            showIntroLoader: true,
            error: null,
            activeRange: "7d",
            lastUpdatedAt: null,
            meta: {
                total: 0,
                capped: false,
            },
            refreshTimer: null,
            introStartedAt: null,
            introRevealTimer: null,
            minimumIntroDurationMs: 1650,
            grid: {
                left: "3%",
                right: "3%",
                bottom: "4%",
                containLabel: true,
            },
        };
    },
    computed: {
        restaurantId() {
            return Number.parseInt(this.$route.params.restaurant_id, 10);
        },
        restaurant() {
            return this.$store.getters["restaurants/findRestaurantById"](this.restaurantId) || null;
        },
        restaurantName() {
            return this.restaurant?.name || "Tableau de bord commande à table";
        },
        currency() {
            return this.restaurant?.currency || this.orders.find((order) => order?.currency)?.currency || "EUR";
        },
        rangeOptions() {
            return RANGE_OPTIONS;
        },
        selectedRange() {
            return RANGE_OPTIONS.find((option) => option.value === this.activeRange) || RANGE_OPTIONS[1];
        },
        periodLabel() {
            return this.selectedRange.value === "today" ? "Commandes du jour" : `Commandes des ${this.selectedRange.days} derniers jours`;
        },
        trendTitle() {
            return this.selectedRange.value === "today"
                ? "Tendance des 7 derniers jours pour donner du contexte"
                : `Volume et montant commandé sur les ${this.selectedRange.days} derniers jours`;
        },
        recentOrdersSubtitle() {
            return this.hasFilteredOrders
                ? `Les ${Math.min(this.recentOrders.length, 5)} dernières commandes de la période`
                : "Aucune activité récente";
        },
        rangeStartDate() {
            const now = startOfDay(new Date());

            if (this.selectedRange.days === 1) {
                return now;
            }

            const start = new Date(now);
            start.setDate(start.getDate() - (this.selectedRange.days - 1));
            return start;
        },
        trendRangeDays() {
            return this.selectedRange.value === "today" ? 7 : this.selectedRange.days;
        },
        trendStartDate() {
            const now = startOfDay(new Date());
            const start = new Date(now);
            start.setDate(start.getDate() - (this.trendRangeDays - 1));
            return start;
        },
        filteredOrders() {
            const start = this.rangeStartDate.getTime();
            return this.orders.filter((order) => {
                const createdAt = new Date(order.created_at).getTime();
                return Number.isFinite(createdAt) && createdAt >= start;
            });
        },
        todayOrders() {
            const todayStart = startOfDay(new Date()).getTime();
            return this.orders.filter((order) => {
                const createdAt = new Date(order.created_at).getTime();
                return Number.isFinite(createdAt) && createdAt >= todayStart;
            });
        },
        billableOrders() {
            return this.filteredOrders.filter((order) => !EXCLUDED_AMOUNT_STATUSES.includes(order.status));
        },
        todayBillableOrders() {
            return this.todayOrders.filter((order) => !EXCLUDED_AMOUNT_STATUSES.includes(order.status));
        },
        openOrdersCount() {
            return this.filteredOrders.filter((order) => OPEN_STATUSES.includes(order.status)).length;
        },
        activeTablesCount() {
            return new Set(
                this.filteredOrders
                    .filter((order) => OPEN_STATUSES.includes(order.status))
                    .map((order) => this.getTable(order))
                    .filter((label) => label && label !== "—")
            ).size;
        },
        averageTicketCents() {
            if (this.billableOrders.length === 0) {
                return 0;
            }

            return Math.round(this.sumOrderAmount(this.billableOrders) / this.billableOrders.length);
        },
        todayAmountCents() {
            return this.sumOrderAmount(this.todayBillableOrders);
        },
        statCards() {
            return [
                {
                    label: "Commandes aujourd'hui",
                    value: this.todayOrders.length,
                    meta: `${this.todayBillableOrders.length} valorisées`,
                    tone: "neutral",
                },
                {
                    label: "Montant commandé",
                    value: this.formatMoney(this.todayAmountCents),
                    meta: "Hors commandes annulées ou échouées",
                    tone: "success",
                },
                {
                    label: "Ticket moyen",
                    value: this.billableOrders.length ? this.formatMoney(this.averageTicketCents) : "—",
                    meta: this.periodLabel,
                    tone: "info",
                },
                {
                    label: "Commandes en cours",
                    value: this.openOrdersCount,
                    meta: `${this.activeTablesCount} table${this.activeTablesCount > 1 ? "s" : ""} active${this.activeTablesCount > 1 ? "s" : ""}`,
                    tone: "warning",
                },
            ];
        },
        trendBuckets() {
            const buckets = [];
            const start = startOfDay(this.trendStartDate);

            for (let index = 0; index < this.trendRangeDays; index += 1) {
                const current = new Date(start);
                current.setDate(start.getDate() + index);
                buckets.push({
                    key: dayKey(current),
                    label: current.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }),
                    count: 0,
                    amount: 0,
                });
            }

            const bucketMap = buckets.reduce((carry, bucket) => {
                carry[bucket.key] = bucket;
                return carry;
            }, {});

            this.orders.forEach((order) => {
                const createdAt = new Date(order.created_at);
                if (Number.isNaN(createdAt.getTime()) || createdAt < start) {
                    return;
                }

                const bucket = bucketMap[dayKey(createdAt)];
                if (!bucket) {
                    return;
                }

                bucket.count += 1;
                if (!EXCLUDED_AMOUNT_STATUSES.includes(order.status)) {
                    bucket.amount += Number(order.total_cents || 0) / 100;
                }
            });

            return buckets;
        },
        hasTrendData() {
            return this.trendBuckets.some((bucket) => bucket.count > 0);
        },
        flowChartOption() {
            return {
                color: ["#0f766e", "#f97316"],
                tooltip: {
                    trigger: "axis",
                },
                legend: {
                    top: 0,
                },
                grid: this.grid,
                xAxis: {
                    type: "category",
                    boundaryGap: true,
                    axisLine: {
                        lineStyle: {
                            color: "#cbd5e1",
                        },
                    },
                    axisLabel: {
                        color: "#64748b",
                    },
                    data: this.trendBuckets.map((bucket) => bucket.label),
                },
                yAxis: [
                    {
                        type: "value",
                        name: "Montant (€)",
                        axisLabel: {
                            color: "#64748b",
                        },
                        splitLine: {
                            lineStyle: {
                                color: "#e2e8f0",
                            },
                        },
                    },
                    {
                        type: "value",
                        name: "Commandes",
                        axisLabel: {
                            color: "#64748b",
                        },
                    },
                ],
                series: [
                    {
                        name: "Montant",
                        type: "bar",
                        barMaxWidth: 28,
                        itemStyle: {
                            borderRadius: [10, 10, 0, 0],
                        },
                        data: this.trendBuckets.map((bucket) => Number(bucket.amount.toFixed(2))),
                    },
                    {
                        name: "Commandes",
                        type: "line",
                        smooth: true,
                        yAxisIndex: 1,
                        symbolSize: 8,
                        lineStyle: {
                            width: 3,
                        },
                        data: this.trendBuckets.map((bucket) => bucket.count),
                    },
                ],
            };
        },
        statusBreakdown() {
            const counts = this.filteredOrders.reduce((carry, order) => {
                carry[order.status] = (carry[order.status] || 0) + 1;
                return carry;
            }, {});

            return Object.entries(counts)
                .map(([status, count]) => ({
                    status,
                    value: count,
                    label: STATUS_META[status]?.label || status,
                    color: STATUS_META[status]?.color || "#64748b",
                }))
                .sort((a, b) => b.value - a.value);
        },
        hasFilteredOrders() {
            return this.filteredOrders.length > 0;
        },
        statusChartOption() {
            return {
                tooltip: {
                    trigger: "item",
                },
                legend: {
                    orient: "vertical",
                    right: 0,
                    top: "middle",
                    textStyle: {
                        color: "#475569",
                    },
                },
                series: [
                    {
                        type: "pie",
                        radius: ["48%", "72%"],
                        center: ["36%", "50%"],
                        avoidLabelOverlap: false,
                        label: {
                            formatter: "{d}%",
                            color: "#0f172a",
                            fontWeight: 600,
                        },
                        itemStyle: {
                            borderColor: "#ffffff",
                            borderWidth: 3,
                        },
                        data: this.statusBreakdown.map((item) => ({
                            value: item.value,
                            name: item.label,
                            itemStyle: { color: item.color },
                        })),
                    },
                ],
            };
        },
        topTables() {
            const grouped = this.filteredOrders.reduce((carry, order) => {
                const key = this.getTable(order);
                if (!key || key === "—") {
                    return carry;
                }

                if (!carry[key]) {
                    carry[key] = {
                        name: key,
                        count: 0,
                    };
                }

                carry[key].count += 1;
                return carry;
            }, {});

            return Object.values(grouped)
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
        },
        topTablesChartOption() {
            return {
                grid: {
                    left: "6%",
                    right: "6%",
                    top: "4%",
                    bottom: "4%",
                    containLabel: true,
                },
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: "shadow",
                    },
                },
                xAxis: {
                    type: "value",
                    axisLabel: {
                        color: "#64748b",
                    },
                    splitLine: {
                        lineStyle: {
                            color: "#e2e8f0",
                        },
                    },
                },
                yAxis: {
                    type: "category",
                    axisLabel: {
                        color: "#334155",
                    },
                    data: this.topTables.map((table) => table.name),
                },
                series: [
                    {
                        type: "bar",
                        data: this.topTables.map((table) => table.count),
                        barWidth: 18,
                        itemStyle: {
                            color: "#0f766e",
                            borderRadius: [0, 10, 10, 0],
                        },
                    },
                ],
            };
        },
        recentOrders() {
            return [...(this.filteredOrders.length ? this.filteredOrders : this.orders)]
                .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime())
                .slice(0, 5);
        },
        quickLinks() {
            return [
                {
                    title: "Voir les commandes",
                    description: "Suivre la liste complète et filtrer par date ou statut.",
                    icon: "fa fa-list-ul",
                    to: { name: "ordering_table.restaurants.order", params: { restaurant_id: this.restaurantId } },
                },
                {
                    title: "Produits",
                    description: "Contrôler les catégories publiées et l'ordre d'affichage.",
                    icon: "fa fa-cutlery",
                    to: { name: "ordering_table.restaurants.products", params: { restaurant_id: this.restaurantId } },
                },
                {
                    title: "En cuisine",
                    description: "Accéder directement aux commandes à préparer.",
                    icon: "fa fa-fire",
                    to: { name: "ordering_table.restaurants.inkitchen", params: { restaurant_id: this.restaurantId } },
                },
                {
                    title: "Tables et QR codes",
                    description: "Gérer les tables, les rattachements et les QR codes.",
                    icon: "fa fa-qrcode",
                    to: { name: "ordering_table.restaurants.configuration.tables", params: { restaurant_id: this.restaurantId } },
                },
            ];
        },
    },
    created() {
        this.introStartedAt = Date.now();
        this.fetchDashboard();
    },
    mounted() {
        this.startAutoRefresh();
    },
    beforeDestroy() {
        this.stopAutoRefresh();
        this.clearIntroRevealTimer();
    },
    methods: {
        fetchDashboard() {
            const isInitialLoad = this.orders.length === 0 && this.lastUpdatedAt === null;

            if (isInitialLoad) {
                this.loading = true;
            } else {
                this.refreshing = true;
            }

            this.error = null;

            const params = new URLSearchParams();
            params.set("status", "all");
            params.set("include_items", "0");
            params.set("include_items_count", "1");
            params.set("per_page", "all");

            this.httpGet(`/api2/v1/${this.restaurantId}/ordering-table/orders?${params.toString()}`)
                .then((response) => {
                    if (response === false) {
                        throw new Error("Dashboard request failed");
                    }

                    const payload = response?.data || {};
                    this.orders = Array.isArray(payload.data) ? payload.data : [];

                    const pagination = payload?.meta?.pagination || {};
                    const total = Number.parseInt(pagination.total, 10);
                    this.meta.total = Number.isFinite(total) ? total : this.orders.length;
                    this.meta.capped = Boolean(pagination.capped);
                    this.lastUpdatedAt = new Date();
                })
                .catch(() => {
                    if (isInitialLoad) {
                        this.error = "Impossible de charger le tableau de bord de la commande à table.";
                    }
                })
                .finally(() => {
                    if (isInitialLoad) {
                        this.loading = false;
                        this.scheduleIntroReveal();
                    } else {
                        this.loading = false;
                    }
                    this.refreshing = false;
                });
        },
        scheduleIntroReveal() {
            const elapsed = Date.now() - (this.introStartedAt || Date.now());
            const remaining = Math.max(0, this.minimumIntroDurationMs - elapsed);

            this.clearIntroRevealTimer();
            this.introRevealTimer = window.setTimeout(() => {
                this.showIntroLoader = false;
                this.introRevealTimer = null;
            }, remaining);
        },
        clearIntroRevealTimer() {
            if (this.introRevealTimer) {
                window.clearTimeout(this.introRevealTimer);
                this.introRevealTimer = null;
            }
        },
        startAutoRefresh() {
            this.stopAutoRefresh();
            this.refreshTimer = setInterval(() => {
                if (!this.loading && !this.refreshing) {
                    this.fetchDashboard();
                }
            }, 60000);
        },
        stopAutoRefresh() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
                this.refreshTimer = null;
            }
        },
        sumOrderAmount(orders) {
            return orders.reduce((total, order) => total + Number(order.total_cents || 0), 0);
        },
        formatMoney(cents) {
            if (cents == null) {
                return "—";
            }

            return (Number(cents) / 100).toLocaleString("fr-FR", {
                style: "currency",
                currency: this.currency,
            });
        },
        formatDate(date) {
            if (!date) {
                return "—";
            }

            return new Date(date).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        formatTime(date) {
            if (!date) {
                return "—";
            }

            return new Date(date).toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        getTable(order) {
            if (!order) {
                return "—";
            }

            if (order.tables?.name) {
                return order.tables.name;
            }

            return order.table_label_snapshot || "—";
        },
        itemCount(order) {
            if (order?.items_sum_quantity != null) {
                return Number(order.items_sum_quantity);
            }
            if (Array.isArray(order?.items)) {
                return order.items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
            }
            if (order?.itemsCount != null) return order.itemsCount;
            if (order?.items_count != null) return order.items_count;
            return 0;
        },
        statusLabel(status) {
            return STATUS_META[status]?.label || status || "Inconnu";
        },
        statusTone(status) {
            return STATUS_META[status]?.tone || "neutral";
        },
    },
};
</script>

<style scoped lang="scss">
.ordering-board {
    --board-main: var(--main-color, #083e60);
    --board-accent: var(--main-color-light, #30a4b7);
    --board-surface: #fffdfa;
    --board-surface-soft: #f6f4ef;
    --board-border: rgba(8, 62, 96, 0.18);
    --board-shadow: rgba(8, 62, 96, 0.1);
    --board-text-soft: #5f7185;
    padding: 24px;
    display: grid;
    gap: 36px;
}

.board-content {
    display: grid;
    gap: 26px;
}

.board-loader-fade-enter-active,
.board-loader-fade-leave-active {
    transition:
        opacity 0.45s ease,
        transform 0.45s ease;
}

.board-loader-fade-enter,
.board-loader-fade-leave-to {
    opacity: 0;
    transform: translateY(14px);
}

.board-hero {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    gap: 24px;
    min-height: 272px;
    padding: 48px 42px;
    border-radius: 28px;
    background:
        radial-gradient(circle at top right, rgba(48, 164, 183, 0.1), transparent 24%),
        radial-gradient(circle at left center, rgba(8, 62, 96, 0.1), transparent 22%),
        linear-gradient(180deg, rgba(8, 62, 96, 0.045) 0%, rgba(255, 255, 255, 0) 100%), var(--board-surface);
    border: 1px solid var(--board-border);
    box-shadow: 0 22px 46px var(--board-shadow);
}

.board-eyebrow {
    margin-bottom: 10px;
    color: var(--board-main);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.board-title {
    margin: 0 0 14px 0;
    color: #0f172a;
    font-size: 36px;
    font-weight: 800;
    line-height: 1.05;
}

.board-subtitle {
    max-width: 720px;
    margin: 0;
    color: var(--board-text-soft);
    font-size: 15px;
    line-height: 1.75;
}

.board-hero__actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    gap: 10px;
}

.board-updated {
    color: var(--board-text-soft);
    font-size: 13px;
}

.board-range-switcher {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px;
}

.board-range-switcher__button {
    padding: 10px 16px;
    border: 1px solid var(--board-border);
    border-radius: 999px;
    background: var(--board-surface);
    color: var(--board-text-soft);
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    transition: all 0.2s ease;
}

.board-range-switcher__button:hover {
    border-color: rgba(8, 62, 96, 0.26);
    color: var(--board-main);
}

.board-range-switcher__button--active {
    border-color: var(--board-main);
    background: var(--board-main);
    color: #ffffff;
}

.board-alert {
    margin-top: 2px;
    margin-bottom: 0;
}

.board-stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 22px;
    align-items: stretch;
    margin-top: 10px;
}

.stat-card {
    position: relative;
    min-height: 252px;
    display: flex;
    flex-direction: column;
    gap: 26px;
    padding: 40px 30px 34px;
    border-radius: 22px;
    border: 1px solid var(--board-border);
    background: linear-gradient(180deg, rgba(8, 62, 96, 0.04) 0%, #ffffff 100%);
    box-shadow: 0 16px 30px var(--board-shadow);
    overflow: hidden;
}

.stat-card::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--board-main);
    opacity: 0.34;
}

.stat-card--success {
    background: linear-gradient(180deg, rgba(48, 164, 183, 0.11) 0%, #ffffff 100%);
}

.stat-card--success::before {
    background: var(--board-accent);
    opacity: 0.62;
}

.stat-card--warning {
    background: linear-gradient(180deg, rgba(8, 62, 96, 0.09) 0%, #ffffff 100%);
}

.stat-card--warning::before {
    background: var(--board-main);
    opacity: 0.56;
}

.stat-card--info {
    background: linear-gradient(180deg, rgba(48, 164, 183, 0.08) 0%, #ffffff 100%);
}

.stat-card--info::before {
    background: linear-gradient(90deg, var(--board-main) 0%, var(--board-accent) 100%);
    opacity: 0.58;
}

.stat-card__label {
    color: var(--board-text-soft);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
}

.stat-card__value {
    color: #0f172a;
    font-size: 42px;
    font-weight: 800;
    line-height: 1;
}

.stat-card__meta {
    margin-top: auto;
    color: var(--board-text-soft);
    font-size: 13px;
    line-height: 1.6;
}

.board-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.95fr);
    gap: 22px;
    margin-top: 14px;
}

.board-panel {
    min-height: 100%;
    padding: 24px;
    border-radius: 24px;
    border: 1px solid var(--board-border);
    background: linear-gradient(180deg, rgba(8, 62, 96, 0.03) 0%, #ffffff 100%);
    box-shadow: 0 12px 26px var(--board-shadow);
}

.board-panel--wide {
    grid-column: 1 / -1;
}

.board-panel__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 18px;

    h2 {
        margin: 0 0 6px 0;
        color: #0f172a;
        font-size: 18px;
        font-weight: 700;
    }

    p {
        margin: 0;
        color: var(--board-text-soft);
        font-size: 13px;
        line-height: 1.5;
    }
}

.chart-shell {
    height: 320px;
}

.chart-shell--compact {
    height: 280px;
}

.chart {
    height: 100%;
    width: 100%;
}

.recent-orders {
    display: grid;
    gap: 12px;
}

.recent-order {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border-radius: 18px;
    background: var(--board-surface-soft);
    border: 1px solid rgba(8, 62, 96, 0.12);
}

.recent-order__main {
    min-width: 0;
}

.recent-order__title {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #0f172a;
    font-size: 15px;
    font-weight: 700;
}

.recent-order__id {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 54px;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(8, 62, 96, 0.1);
    color: var(--board-main);
    font-size: 12px;
}

.recent-order__meta {
    margin-top: 6px;
    color: #64748b;
    font-size: 13px;
}

.recent-order__side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    text-align: right;
}

.recent-order__amount {
    color: #0f172a;
    font-size: 15px;
    font-weight: 700;
}

.recent-order__date {
    color: #64748b;
    font-size: 12px;
}

.status-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.status-pill--warning {
    background: #fff7ed;
    color: #c2410c;
}

.status-pill--success {
    background: #ecfdf5;
    color: #047857;
}

.status-pill--info {
    background: #eff6ff;
    color: #1d4ed8;
}

.status-pill--danger {
    background: #fef2f2;
    color: #b91c1c;
}

.status-pill--neutral {
    background: #eef2ff;
    color: #4338ca;
}

.quick-links {
    display: grid;
    gap: 12px;
}

.quick-link {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 16px;
    border-radius: 18px;
    border: 1px solid var(--board-border);
    background: linear-gradient(180deg, rgba(8, 62, 96, 0.035) 0%, #ffffff 100%);
    color: inherit;
    text-decoration: none;
    transition: all 0.2s ease;
}

.quick-link:hover {
    transform: translateY(-1px);
    border-color: rgba(8, 62, 96, 0.24);
    box-shadow: 0 14px 24px rgba(8, 62, 96, 0.08);
}

.quick-link__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(48, 164, 183, 0.08) 0%, rgba(8, 62, 96, 0.06) 100%);
    color: var(--board-main);
    font-size: 18px;
    flex: 0 0 auto;
}

.quick-link__content {
    display: grid;
    gap: 4px;
}

.quick-link__title {
    color: #0f172a;
    font-size: 14px;
    font-weight: 700;
}

.quick-link__description {
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
}

.panel-empty,
.board-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 160px;
    padding: 22px;
    border-radius: 24px;
    background: linear-gradient(180deg, rgba(8, 62, 96, 0.025) 0%, var(--board-surface-soft) 100%);
    color: var(--board-text-soft);
    text-align: center;
}

.board-state {
    min-height: 220px;
}

.board-state-error {
    background: #fef2f2;
    color: #b91c1c;
}

@media (max-width: 1199px) {
    .board-stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .board-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767px) {
    .ordering-board {
        padding: 16px;
    }

    .board-hero {
        flex-direction: column;
        padding: 28px 24px;
        min-height: 0;
    }

    .board-hero__actions {
        width: 100%;
        align-items: stretch;
    }

    .board-stats {
        grid-template-columns: 1fr;
    }

    .stat-card {
        min-height: 0;
        padding: 30px 24px 24px;
    }

    .recent-order {
        flex-direction: column;
        align-items: flex-start;
    }

    .recent-order__side {
        width: 100%;
        align-items: flex-start;
        text-align: left;
    }
}
</style>
